import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Building2, Search, Globe, ClipboardList, Users, Link, BarChart2 } from 'lucide-react';
import { Button, Modal, EmptyState, Badge, RespondersModal } from '../components/common';
import type { Organization, CreateOrganizationPayload, UpdateOrganizationPayload, Survey, SurveyTemplate } from '../types';
import { getRespondersByOrganization, getRespondersByInstance } from '../services/api';
import { copyRegisterLink } from '../utils/clipboard';

interface OrganizationsPageProps {
    organizations: Organization[];
    instances: Survey[];
    templates: SurveyTemplate[];
    loading: boolean;
    onCreate: (payload: CreateOrganizationPayload) => Promise<void>;
    onUpdate: (payload: UpdateOrganizationPayload) => Promise<void>;
    onDelete: (orgId: string) => Promise<void>;
    onShowToast: (type: 'success' | 'error' | 'warning' | 'info', title: string, message?: string) => void;
    onNavigateToAnalytics?: (orgId: string) => void;
    onNavigateToInstanceAnalytics?: (instanceId: string) => void;
    onNavigateToRespondent?: (respondentId: string, context: { organizationId?: string; instanceId?: string }) => void;
}

export const OrganizationsPage: React.FC<OrganizationsPageProps> = ({
    organizations,
    instances,
    templates,
    loading,
    onCreate,
    onUpdate,
    onDelete,
    onShowToast,
    onNavigateToAnalytics,
    onNavigateToInstanceAnalytics,
    onNavigateToRespondent,
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [formData, setFormData] = useState({ name: '', allowed_email_domain: '', is_global: false });
    const [submitting, setSubmitting] = useState(false);
    const [viewingOrg, setViewingOrg] = useState<Organization | null>(null);
    const [viewingInstance, setViewingInstance] = useState<Survey | null>(null);
    const [isInstancesModalOpen, setIsInstancesModalOpen] = useState(false);
    const [isRespondersModalOpen, setIsRespondersModalOpen] = useState(false);
    const [isInstanceRespondersModalOpen, setIsInstanceRespondersModalOpen] = useState(false);

    const filteredOrgs = organizations.filter(
        (org) =>
            org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            org.allowed_email_domain?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const openCreateModal = () => {
        setEditingOrg(null);
        setFormData({ name: '', allowed_email_domain: '', is_global: false });
        setIsModalOpen(true);
    };

    const openEditModal = (org: Organization) => {
        setEditingOrg(org);
        setFormData({ name: org.name, allowed_email_domain: org.allowed_email_domain || '', is_global: org.is_global });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (editingOrg) {
                await onUpdate({
                    org_id: editingOrg.id,
                    name: formData.name,
                    allowed_email_domain: formData.allowed_email_domain,
                    is_global: formData.is_global,
                });
            } else {
                await onCreate(formData);
            }
            setIsModalOpen(false);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (org: Organization) => {
        if (window.confirm(`האם למחוק את הארגון "${org.name}"? פעולה זו אינה הפיכה.`)) {
            await onDelete(org.id);
        }
    };

    const handleViewInstances = (org: Organization) => {
        setViewingOrg(org);
        setIsInstancesModalOpen(true);
    };

    const getOrgInstances = (orgId: string) => {
        return instances.filter(i => i.organization_id === orgId);
    };

    const getTemplateName = (templateId: number) => {
        const template = templates.find(t => t.id == templateId);
        return template?.name || `תבנית #${templateId}`;
    };

    const handleCopyRegisterLink = (instanceId: string) => copyRegisterLink(instanceId, onShowToast);

    return (
        <div>
            {/* Header Actions */}
            <div className="page-header-actions">
                <div className="search-wrapper">
                    <Search
                        size={18}
                        className="search-icon"
                    />
                    <input
                        type="text"
                        placeholder="חיפוש ארגונים..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <Button icon={<Plus size={18} />} onClick={openCreateModal}>
                    ארגון חדש
                </Button>
            </div>

            {/* Organizations Table */}
            <div className="card">
                {loading ? (
                    <div className="loading-overlay">
                        <div className="spinner" />
                        <span className="loading-text">טוען ארגונים...</span>
                    </div>
                ) : organizations.length === 0 ? (
                    <EmptyState
                        icon={<Building2 size={64} />}
                        title="אין ארגונים עדיין"
                        description="הוסף ארגון חדש כדי להתחיל לנהל סקרים"
                        action={
                            <Button icon={<Plus size={18} />} onClick={openCreateModal}>
                                ארגון חדש
                            </Button>
                        }
                    />
                ) : (
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>שם הארגון</th>
                                    <th>סוג</th>
                                    <th>דומיין מורשה</th>
                                    <th>תאריך יצירה</th>
                                    <th>פעולות</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrgs.map((org) => (
                                    <tr key={org.id}>
                                        <td data-label="שם הארגון">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                                                <div
                                                    style={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: 'var(--radius-lg)',
                                                        background: 'linear-gradient(135deg, var(--primary-100), var(--primary-200))',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: 'var(--primary-600)',
                                                    }}
                                                >
                                                    <Building2 size={18} />
                                                </div>
                                                <span style={{ fontWeight: 500 }}>{org.name}</span>
                                            </div>
                                        </td>
                                        <td data-label="סוג">
                                            {org.is_global ? (
                                                <Badge variant="success">גלובלי</Badge>
                                            ) : (
                                                <Badge variant="neutral">מקומי</Badge>
                                            )}
                                        </td>
                                        <td data-label="דומיין מורשה">
                                            {org.allowed_email_domain ? (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                                    <Globe size={14} style={{ color: 'var(--neutral-400)' }} />
                                                    <span>{org.allowed_email_domain}</span>
                                                </div>
                                            ) : (
                                                <Badge variant="neutral">ללא הגבלה</Badge>
                                            )}
                                        </td>
                                        <td data-label="תאריך יצירה" style={{ color: 'var(--neutral-500)' }}>
                                            {new Date(org.created_at).toLocaleDateString('he-IL')}
                                        </td>
                                        <td data-label="פעולות">
                                            <div className="cell-actions">
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleViewInstances(org)}
                                                    title="צפה בקבוצות סקר"
                                                >
                                                    <ClipboardList size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => {
                                                        setViewingOrg(org);
                                                        setIsRespondersModalOpen(true);
                                                    }}
                                                    title="צפה במשיבים"
                                                >
                                                    <Users size={16} />
                                                </button>
                                                {onNavigateToAnalytics && (
                                                    <button
                                                        className="btn btn-icon btn-ghost sm"
                                                        onClick={() => onNavigateToAnalytics(org.id)}
                                                        title="ניתוח תוצאות"
                                                        style={{ color: 'var(--primary-400)' }}
                                                    >
                                                        <BarChart2 size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => openEditModal(org)}
                                                    title="עריכה"
                                                >
                                                    <Pencil size={16} />
                                                </button>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleDelete(org)}
                                                    title="מחיקה"
                                                    style={{ color: 'var(--error-500)' }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Create/Edit Modal */}
            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={editingOrg ? 'עריכת ארגון' : 'ארגון חדש'}
                footer={
                    <>
                        <Button onClick={handleSubmit} loading={submitting}>
                            {editingOrg ? 'שמור שינויים' : 'צור ארגון'}
                        </Button>
                        <Button variant="secondary" onClick={() => setIsModalOpen(false)}>
                            ביטול
                        </Button>
                    </>
                }
            >
                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label>שם הארגון *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            placeholder="לדוגמה: חברת ABC"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label>דומיין אימייל מורשה</label>
                        <input
                            type="text"
                            value={formData.allowed_email_domain}
                            onChange={(e) => setFormData({ ...formData, allowed_email_domain: e.target.value })}
                            placeholder="לדוגמה: company.com"
                        />
                        <span className="form-help">
                            רק משתמשים עם כתובות אימייל מדומיין זה יוכלו למלא את הסקר
                        </span>
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '12px' }}>
                        <input
                            type="checkbox"
                            id="is_global"
                            checked={formData.is_global}
                            onChange={(e) => setFormData({ ...formData, is_global: e.target.checked })}
                            style={{ width: 'auto', margin: 0 }}
                        />
                        <label htmlFor="is_global" style={{ margin: 0, fontWeight: 500 }}>ארגון גלובלי (ללא השוואה קבוצתית)</label>
                    </div>
                </form>
            </Modal>

            {/* View Instances Modal */}
            <Modal
                isOpen={isInstancesModalOpen}
                onClose={() => setIsInstancesModalOpen(false)}
                title={`קבוצות סקרים: ${viewingOrg?.name}`}
                size="large"
            >
                {viewingOrg && (
                    <div className="table-container">
                        {getOrgInstances(viewingOrg.id).length === 0 ? (
                            <div style={{ padding: 'var(--space-10)', textAlign: 'center', color: 'var(--neutral-500)' }}>
                                לא נמצאו קבוצות סקרים לארגון זה
                            </div>
                        ) : (
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>שם הסקר</th>
                                        <th>תבנית</th>
                                        <th>סטטוס</th>
                                        <th>תאריך יצירה</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {getOrgInstances(viewingOrg.id).map((instance) => (
                                        <tr key={instance.id}>
                                            <td style={{ fontWeight: 500 }}>{instance.name}</td>
                                            <td>{getTemplateName(instance.template_id)}</td>
                                            <td>
                                                <Badge variant={instance.is_active ? 'success' : 'neutral'}>
                                                    {instance.is_active ? 'פעיל' : 'לא פעיל'}
                                                </Badge>
                                            </td>
                                            <td style={{ color: 'var(--neutral-500)' }}>
                                                {new Date(instance.created_at).toLocaleDateString('he-IL')}
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => handleCopyRegisterLink(instance.id)}
                                                    title="העתק קישור להרשמה"
                                                >
                                                    <Link size={16} />
                                                </button>
                                                {onNavigateToInstanceAnalytics && (
                                                    <button
                                                        className="btn btn-icon btn-ghost sm"
                                                        onClick={() => onNavigateToInstanceAnalytics(instance.id)}
                                                        title="ניתוח תוצאות"
                                                        style={{ color: 'var(--primary-400)' }}
                                                    >
                                                        <BarChart2 size={16} />
                                                    </button>
                                                )}
                                                <button
                                                    className="btn btn-icon btn-ghost sm"
                                                    onClick={() => {
                                                        setViewingInstance(instance);
                                                        setIsInstanceRespondersModalOpen(true);
                                                    }}
                                                    title="צפה במשיבים"
                                                >
                                                    <Users size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </Modal>

            <RespondersModal
                isOpen={isRespondersModalOpen}
                onClose={() => setIsRespondersModalOpen(false)}
                title={`משיבים בארגון: ${viewingOrg?.name}`}
                fetchResponders={() => getRespondersByOrganization(viewingOrg?.id || '')}
                onNavigateToAnalytics={(respondentId) => onNavigateToRespondent?.(respondentId, { organizationId: viewingOrg?.id })}
            />

            <RespondersModal
                isOpen={isInstanceRespondersModalOpen}
                onClose={() => setIsInstanceRespondersModalOpen(false)}
                title={`משיבים בסקר: ${viewingInstance?.name}`}
                fetchResponders={() => getRespondersByInstance(viewingInstance?.id || '')}
                onNavigateToAnalytics={(respondentId) => onNavigateToRespondent?.(respondentId, { organizationId: viewingOrg?.id, instanceId: viewingInstance?.id })}
            />
        </div>
    );
};
